/**
 * DiscoveryDrawerShell - Unified drawer shell for Search and Filter modes
 *
 * Shared structure:
 * - Backdrop with click-to-close
 * - Canonical bottom-sheet presentation for public Discover Search/Filters
 * - Compatibility anchored presentation for existing non-Phase-1A consumers
 * - Unified header with:
 *   - Visual breathing room at top (calm, no back-strip)
 *   - Center: title/search input area (mode-specific)
 *   - Right: mode toggle (Filter icon) + close X
 * - Reserved rail slot (always mounted, fixed height for pills/variants)
 * - Scrollable body area
 * - Optional sticky footer
 *
 * UX: NO "Back to..." strip - this is a continuous stacked experience.
 *
 * @version 2.0.0
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/utils';
import { X, SlidersHorizontal, Search } from 'lucide-react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

export type DrawerMode = 'filter' | 'search';
export type DiscoveryDrawerPresentation = 'anchored' | 'bottom-sheet';

export interface DiscoveryDrawerShellProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Current mode */
  mode: DrawerMode;
  /** Switch to the other mode (for mode toggle button) */
  onModeToggle?: () => void;

  /**
   * Visual presentation.
   * `anchored` preserves existing compatibility consumers.
   * Canonical public Discover Search/Filters explicitly use `bottom-sheet`.
   */
  presentation?: DiscoveryDrawerPresentation;

  // Header configuration
  /** Show back button (for variant selection state) */
  showBack?: boolean;
  /** Back button handler */
  onBack?: () => void;
  /** Back button label */
  backLabel?: string;

  // Center header content (mode-specific)
  /** Custom center content for header (e.g., search input or title) */
  headerCenter?: React.ReactNode;
  /** Fallback title if no headerCenter provided */
  title?: string;

  // Reserved rail slot content (always has fixed height)
  /** Content for the variant/pills rail (always mounted) */
  railContent?: React.ReactNode;

  // Body content
  children: React.ReactNode;

  // Footer
  /** Optional sticky footer content */
  footer?: React.ReactNode;
}

// Fixed height for the rail slot (reserves space even when empty)
const RAIL_SLOT_HEIGHT = 56; // matches ~h-14

export const DiscoveryDrawerShell: React.FC<DiscoveryDrawerShellProps> = ({
  open,
  onClose,
  mode,
  onModeToggle,
  presentation = 'anchored',
  showBack = false,
  onBack,
  backLabel = 'Back',
  headerCenter,
  title,
  railContent,
  children,
  footer,
}) => {
  // Ensure we only portal after mount (SSR safety)
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const isBottomSheet = presentation === 'bottom-sheet';
  const [sheetPresent, setSheetPresent] = React.useState(open);
  const [sheetVisible, setSheetVisible] = React.useState(false);

  // Bottom-sheet mode stays mounted briefly during close so the panel can
  // animate back down instead of disappearing instantly.
  React.useEffect(() => {
    if (!isBottomSheet) return;

    let frame: number | undefined;
    let timeout: number | undefined;

    if (open) {
      setSheetPresent(true);
      frame = window.requestAnimationFrame(() => {
        setSheetVisible(true);
      });
    } else {
      setSheetVisible(false);
      timeout = window.setTimeout(() => {
        setSheetPresent(false);
      }, 250);
    }

    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [isBottomSheet, open]);

  const shouldRender = isBottomSheet ? sheetPresent : open;

  // Strong body scroll lock using position:fixed pattern.
  // Keep the lock through the bottom-sheet exit transition.
  useBodyScrollLock(isBottomSheet ? sheetPresent : open);

  if (!shouldRender || !mounted) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[1000] flex flex-col md:hidden overflow-hidden',
        isBottomSheet && 'transition-opacity duration-200',
        isBottomSheet && (sheetVisible ? 'opacity-100' : 'opacity-0'),
      )}
      data-discovery-presentation={presentation}
    >
      {/* Backdrop (dims feed, click to close) */}
      <div className="absolute inset-0 bg-black/40 z-[1000]" onClick={onClose} />

      {/* Existing shell; canonical public mode enters and exits from below. */}
      <div
        className={cn(
          'z-[1001] flex flex-col bg-background rounded-t-2xl overflow-hidden',
          isBottomSheet
            ? 'absolute inset-x-0 bottom-0 shadow-xl transition-transform duration-250 ease-out will-change-transform'
            : 'relative mt-[56px]',
          isBottomSheet && (sheetVisible ? 'translate-y-0' : 'translate-y-full'),
        )}
        style={{ height: 'calc(100dvh - 56px)' }}
        data-discovery-drawer
        data-drawer="true"
        data-discovery-bottom-sheet={isBottomSheet ? 'true' : undefined}
        role="dialog"
        aria-modal="true"
      >
        {/* ===== UNIFIED HEADER ===== */}
        <div className="sticky top-0 z-10 shrink-0 border-b border-border bg-background">
          {/* Breathing room / visual spacing at top (replaces back-strip) */}
          <div className="h-2 bg-muted/10" aria-hidden="true" />

          {/* Main header row */}
          <div className="px-4 py-3 flex items-center gap-3">
            {/* Center: custom content or title */}
            <div className="flex-1 min-w-0">
              {headerCenter || (
                <div className="flex items-center gap-2">
                  {mode === 'filter' ? (
                    <SlidersHorizontal className="w-5 h-5 text-muted-foreground shrink-0" />
                  ) : (
                    <Search className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  <span className="font-medium text-foreground truncate">
                    {title || (mode === 'filter' ? 'Filters' : 'Search')}
                  </span>
                </div>
              )}
            </div>

            {/* Right controls: mode toggle + close */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Mode toggle button (only if handler provided) */}
              {onModeToggle && (
                <button
                  onClick={onModeToggle}
                  className={cn(
                    'h-9 w-9 flex items-center justify-center rounded-full transition-colors',
                    mode === 'filter'
                      ? 'bg-muted text-muted-foreground hover:text-foreground'
                      : 'bg-primary/10 text-primary',
                  )}
                  aria-label={mode === 'filter' ? 'Switch to search' : 'Switch to filters'}
                >
                  {mode === 'filter' ? (
                    <Search className="w-4 h-4" />
                  ) : (
                    <SlidersHorizontal className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Close button - ALWAYS visible */}
              <button
                onClick={onClose}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-muted text-foreground hover:bg-muted/80 transition-colors"
                aria-label="Close"
                data-close-button
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ===== RESERVED RAIL SLOT (always mounted, fixed height) ===== */}
        {/* SINGLE OWNER: Only this shell renders the variant rail */}
        <div
          className="shrink-0 border-b border-border bg-muted/20"
          style={{ height: RAIL_SLOT_HEIGHT }}
          data-variant-rail="true"
        >
          <div className="h-full px-4 flex items-center overflow-x-auto scrollbar-hide">
            {railContent}
          </div>
        </div>

        {/* ===== SCROLLABLE BODY ===== */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</div>

        {/* ===== OPTIONAL STICKY FOOTER ===== */}
        {footer && (
          <div className="sticky bottom-0 z-10 shrink-0 border-t border-border bg-background">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DiscoveryDrawerShell;
